import { Component, computed, inject, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { Store } from '@ngrx/store';
import { SigIdPaymentComponentRead, SigIdPaymentRead } from '../../../../services/sig-id.common.model';
import { selectLoading, selectPayerData, selectPayment } from '../../../../store/selectors/my-bills.selectors';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { loadPayerServices, loadPayment } from '../../../../store/actions/my-bills.actions';
import { BreadcrumbsComponent } from '../../../../common/breadcrumbs/breadcrumbs.component';
import { ContentComponent } from '../../../../components/content/content.component';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../../../../common/spinner/spinner.component';
import { LabelComponent } from '../../../../common/label/label.component';
import { CopyToClipboardDirective } from '../../../../directives/copy-to-clipboard.directive';
import { ToNumberPipe } from '../../../../pipes/to-number.pipe';
import { AlertComponent } from '../../../../common/alert/alert.component';
import { calcItemTotalAmount, ItemTotalAmountPipe } from '../../pipes/item-total-amount.pipe';
import { calcTotalAmount, TotalAmountPipe } from '../../pipes/total-amount.pipe';
import { PaymentCommonService } from '../../../../services/payment.common.service';
import { environment } from '../../../../../environments/environment';
import { SessionService } from '../../../../services/ssession.service';
import { SigidPayerServiceData } from '../../../../openapi';

@Component({
  selector: 'app-my-bills-payment-page',
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    ContentComponent,
    RouterLink,
    CommonModule,
    SpinnerComponent,
    LabelComponent,
    CopyToClipboardDirective,
    ToNumberPipe,
    AlertComponent,
    ItemTotalAmountPipe,
    TotalAmountPipe
  ],
  styles: `
    @import "../../../../../styles/utilities/media_dimensions";
    .pay-for-all {
      @media (max-width: $max-mob) {
        width: 100%;
      }
    }
  `,
  templateUrl: './my-bills-payment-page.component.html'
})
export class MyBillsPaymentPageComponent implements OnInit {

  public currentDate: Date = new Date();

  private store$: Store = inject(Store);

  private paymentCommonService: PaymentCommonService = inject(PaymentCommonService);

  private route: ActivatedRoute = inject(ActivatedRoute);

  private identSigId: string = this.route.snapshot.params['identSigId'];

  private serviceCode: string = this.route.snapshot.params['serviceCode'];

  private baseReturnUrl: string = `${environment.ssl ? 'https' : 'http'}://${window.location.host}/moje-oplaty/status-platnosci/${this.identSigId}/${this.serviceCode}`;

  public payment: Signal<SigIdPaymentRead | null> = this.store$.selectSignal(selectPayment);

  public payerData: Signal<SigidPayerServiceData | null> = this.store$.selectSignal(selectPayerData(this.identSigId));

  public componentsForSelect: Signal<SigIdPaymentComponentRead[]> = computed(() => this.payment()?.components
    ?.filter(v => !!calcItemTotalAmount(v)) || []);

  public selectedComponents: WritableSignal<SigIdPaymentComponentRead[]> = signal([]);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public goToPay(item: SigIdPaymentComponentRead): void {
    this.paymentCommonService.goToPay({
      returnUrl: this.baseReturnUrl + '/' + item?.idZap,
      accountNumberOverride: this.payment()?.service?.accountNumber || undefined,
      clientEmail: SessionService.GetTokenData()?.email || '',
      clientFirstName: SessionService?.GetTokenData()?.firstName || '',
      clientLastName: SessionService?.GetTokenData()?.lastName || '',
      descriptionOverride: `${this.payment()?.service?.code || ''}, ${this.payerData()?.fullName || ''} - ${this.identSigId}`,
      totalAmount: calcItemTotalAmount(item)
    }).subscribe();
  }

  public goToPayForAll(): void {
    this.paymentCommonService.goToPay({
      returnUrl: this.baseReturnUrl,
      accountNumberOverride: this.payment()?.service?.accountNumber || undefined,
      clientEmail: SessionService.GetTokenData()?.email || '',
      clientFirstName: SessionService?.GetTokenData()?.firstName || '',
      clientLastName: SessionService?.GetTokenData()?.lastName || '',
      descriptionOverride: `${this.payment()?.service?.code || ''}, ${this.payerData()?.fullName || ''} - ${this.identSigId}`,
      totalAmount: calcTotalAmount(this.componentsForSelect())
    }).subscribe();
  }

  public goToPayForSelected(): void {
    this.paymentCommonService.goToPay({
      returnUrl: this.baseReturnUrl,
      accountNumberOverride: this.payment()?.service?.accountNumber || undefined,
      clientEmail: SessionService.GetTokenData()?.email || '',
      clientFirstName: SessionService?.GetTokenData()?.firstName || '',
      clientLastName: SessionService?.GetTokenData()?.lastName || '',
      descriptionOverride: `${this.payment()?.service?.code || ''}, ${this.payerData()?.fullName || ''} - ${this.identSigId}`,
      totalAmount: calcTotalAmount(this.selectedComponents())
    }).subscribe();
  }

  public toggleSelectAll(): void {
    const forSelect = this.componentsForSelect();
    if (forSelect.length === this.selectedComponents().length) {
      this.selectedComponents.set([]);
    } else {
      this.selectedComponents.set([...forSelect]);
    }
  }

  public toggleSelect(item: SigIdPaymentComponentRead): void {
    if (this.selectedComponents().includes(item)) {
      this.selectedComponents.update((selected) => {
        selected.splice(selected.indexOf(item), 1);
        return selected;
      });
    } else {
      this.selectedComponents.update((selected) => {
        selected.push(item);
        return selected;
      });
    }
  }

  private loadPayment(): void {
    this.store$.dispatch(loadPayment({
      identSigId: this.identSigId,
      serviceCode: this.serviceCode
    }));
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadPayerServices({}));
    this.loadPayment();
  }
}
